import { inject, Injectable } from '@angular/core';
import { AuthFacade, AuthStrategies } from '@mds/ngx-auth';
import {
  catchError,
  filter,
  map,
  Observable,
  of,
  shareReplay,
  switchMap,
  take,
} from 'rxjs';
import { UserScopesModel } from '../models/user-scopes.model';
import { MeClient, UserClaimsResponse } from './api-client';

@Injectable({
  providedIn: 'root',
})
export class UserScopesService {
  private readonly meClient = inject(MeClient);
  private readonly userScopes$: Observable<UserScopesModel>;

  constructor(private readonly authService: AuthFacade) {
    this.userScopes$ = this.authService
      .isAuthenticated$(AuthStrategies.Tenancy)
      .pipe(
        filter((isAuthenticated) => isAuthenticated),
        take(1),
        switchMap(() =>
          this.meClient.getUserClaims().pipe(
            map((response) => this.parseUserScopesFromClaims(response)),
            catchError(() => of<UserScopesModel>({ scopes: [] })),
          ),
        ),
        shareReplay({ bufferSize: 1, refCount: false }),
      );
  }

  hasScopes(requiredScopes: readonly string[]): Observable<boolean> {
    return this.userScopes$.pipe(
      map((userScopes) =>
        requiredScopes.every((scope) => userScopes.scopes.includes(scope)),
      ),
    );
  }

  private parseUserScopesFromClaims(
    response: UserClaimsResponse,
  ): UserScopesModel {
    const rawScopes: unknown = response?.claims?.['scope'];

    const scopeStrings: string[] = Array.isArray(rawScopes)
      ? rawScopes.filter((value): value is string => typeof value === 'string')
      : [];

    const scopes = new Set<string>();

    for (const scopeValue of scopeStrings) {
      const extracted = this.tryExtractScopePrefix(scopeValue);
      if (extracted) scopes.add(extracted);
    }

    return { scopes: Array.from(scopes) };
  }

  private tryExtractScopePrefix(scopeValue: string): string | null {
    if (!scopeValue) return null;
    if (!URL.canParse(scopeValue)) return null;

    const url = new URL(scopeValue);
    const segments = url.pathname.split('/').filter(Boolean);
    const lastSegment = segments.at(-1);
    return lastSegment ?? null;
  }
}
